import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PurpleButton from '@/Components/shared/purple-button/PurpleButton';
import styles from './approval-mat-form.module.scss';
import { usePostConfirmInfosDeliveryman } from '../../api/post-confirm-infos';
import type { FormType } from './types';
import { APPROVAL_MAT_FORM_SHECMA } from './constants';
import { FooterReasons } from '../footer-reasons';
import { ModalConfirmInfos } from '../modal-confirm-infos';
import type { PreRegisterInfoProps } from '../../types';
import { ApprovalMatSections } from '../approval-mat-sections';

function ApprovalMatForm({
  deliveryman,
}: {
  deliveryman: PreRegisterInfoProps;
}) {
  const navigate = useNavigate();

  const { deliverymanId = '' } = useParams<{ deliverymanId: string }>();

  const [openModalConfirm, setOpenModalConfirm] = useState(false);

  const { mutate: postConfirmInfos, isLoading: isLoadingPostConfirmInfos } =
    usePostConfirmInfosDeliveryman({
      onSuccess: () => {
        navigate(`/entregadores/${deliverymanId}`);
      },
    });

  const showPersonalData =
    !deliveryman?.preRegistration?.hasApprovedPersonalData;
  const showBankData = deliveryman?.contaBancaria;
  const showRg = deliveryman?.preRegistration?.rg;
  const showCnh = deliveryman?.preRegistration?.cnh;
  const showCrlv = deliveryman?.preRegistration?.crlv;
  const showDeliverymanPicture =
    deliveryman?.preRegistration?.deliverymanPictureId;
  const showDeliverymanWithRgPicture =
    deliveryman?.preRegistration?.deliverymanWithRgPictureId;

  const defaultValues = {
    showPersonalData: !!showPersonalData,
    showBankData: !!showBankData,
    showRg: !!showRg,
    showCnh: !!showCnh,
    showCrlv: !!showCrlv,
    showDeliverymanPicture: !!showDeliverymanPicture,
    showDeliverymanWithRgPicture: !!showDeliverymanWithRgPicture,
  };

  const methods = useForm<FormType>({
    resolver: yupResolver(APPROVAL_MAT_FORM_SHECMA),
    defaultValues,
    mode: 'onChange',
  });

  const { isDirty, isValid } = methods.formState;

  const onSubmit = (data: FormType) => {
    postConfirmInfos({
      deliverymanId: deliverymanId!,
      data: {
        deniedReasons: data.valuesReasons?.value || undefined,
        commentsDeniedReasons: data.valuesReasons?.comment || undefined,
        commentsCnh: data.commentsCnh || undefined,
        commentsCrlv: data.commentsCrlv || undefined,
        commentsRg: data.commentsRg || undefined,
        commentsDeliverymanPicture:
          data.commentsDeliverymanPicture || undefined,
        commentsPersonalData: data.commentsPersonalData || undefined,
        commentsBankAccount: data.commentsBankAccount || undefined,
        hasApprovedCnh: data.hasApprovedCnh,
        hasApprovedCrlv: data.hasApprovedCrlv,
        hasApprovedDeliverymanPicture: data.hasApprovedDeliverymanPicture,
        hasApprovedSelfieWithRg: data.hasApprovedSelfieWithRg,
        hasApprovedPersonalData: data.hasApprovedPersonalData,
        hasApprovedRg: data.hasApprovedRg,
        hasApprovedBankAccount: data.hasApprovedBankAccount,
      },
    });
  };

  const onOpenModalConfirm = () => {
    setOpenModalConfirm(true);

    if (methods.getValues('valuesReasons.value')?.length === 0) {
      methods.setValue('valuesReasons.comment', '');
    }
  };

  return (
    <FormProvider {...methods}>
      <ApprovalMatSections deliverymanInfos={deliveryman} />

      <FooterReasons />

      <div className={styles.containerButton}>
        <PurpleButton
          onClick={onOpenModalConfirm}
          className={styles.button}
          disabled={!isDirty || !isValid}
          textFirst="Concluir"
        />
      </div>

      <ModalConfirmInfos
        open={openModalConfirm}
        onClose={() => setOpenModalConfirm(false)}
        onSubmit={methods.handleSubmit(onSubmit)}
        isLoadingButton={isLoadingPostConfirmInfos}
      />
    </FormProvider>
  );
}

export { ApprovalMatForm };
